import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
    ActivityLevelMeta,
    BODY_LABEL,
    TreatFrequencyMeta,
    safeStr,
    ALLERGY_NONE,
    buildDogForStorage,
    HEALTH_ISSUE_NONE,
    HealthIssue,
    REPRO_LABEL,
    Reproduction,
    Sex,
} from "@/helpers/enums";
import {
    calculateDailyIntakeFromForm,
    type DogFormLike,
} from "@/helpers/calorieCalculator";

import { useDog } from "@/state/useDog";
import { useSendQuizToKlaviyo } from "@/domains/klaviyo/klaviyo";
import { quizTestTags } from "@/experiments/quizTests";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "@/components/quiz/shared/quizAssets";
import { getStatDescriptions, getPronoun, getAgeCategory } from "@/helpers/statCopy"; 
import { QuizInlineCTA } from "@/components/quiz/shared/QuizNavBar"; 
type Props = {
    dogs: {
        id: string;
        form: any;
    }[];
    activeDogId: string | null;
    onNext?: () => void; 
};

type StatCardProps = {
    icon: string;
    label: string;
    value: string | number;
    description?: string;
};

type TimelineMilestone = {
    day: number;
    badgeColor: string;
    title: string;
    description: string;
    quote: string;
    reviewer: string;
};

/** Figma profile stat card — white rounded tile, brand icon, RL Aqva value + grey label. */
const StatCard = ({ icon, label, value, description }: StatCardProps) => (
    <Box
        sx={{
            bgcolor: QUIZ.color.white,
            borderRadius: QUIZ.radius.card,
            display: "flex",
            flexDirection: "column",
            px: { xs: "14px", md: "18px" },
            py: { xs: "12px", md: "16px" },
            width: "100%",            height: "100%",
            minWidth: 0,
            boxSizing: "border-box",
        }}
    >
        {/* Top row: reserve space for the value so long labels wrap inside the card. */}
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "38px minmax(0, 1fr)", md: "42px minmax(0, 1fr)" },
                alignItems: "center",
                gap: { xs: "10px", md: "8px" },
                minWidth: 0,
            }}
        >
            <Box
                component="img"
                src={icon}
                alt=""
                sx={{ width: { xs: 38, md: 42 }, height: { xs: 38, md: 42 }, objectFit: "contain" }}
            />
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.display,
                        fontWeight: 900,
                        fontSize: { xs: 17, md: 18 },
                        color: QUIZ.color.ink,
                        lineHeight: 1.1,
                        overflowWrap: "break-word",
                    }}
                >
                    {value}
                </Typography>
                <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 14, md: 15 }, color: QUIZ.color.statLabel }}>
                    {label}
                </Typography>
            </Box>
        </Box>

        {description && (
            <>
                <Box
                    sx={{
                        borderTop: `1px solid ${QUIZ.color.statLabel}`,
                        opacity: 0.2,
                        my: { xs: "10px", md: "12px" },
                    }}
                />
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        fontSize: { xs: 14, md: 15 },
                        color: QUIZ.color.ink,
                        lineHeight: 1.4,
                    }}
                >
                    {description}
                </Typography>
            </>
        )}
    </Box>
);

/**
 * Small reassurance-style card. Shared visual shape for both the health-issues
 * paragraph and the (now separate) allergy paragraph, so the two conditions
 * read consistently but never share a single block of copy.
 */
const ReassuranceCard = ({ icon, children }: { icon: string; children: React.ReactNode }) => (
    <Box
        sx={{
            bgcolor: QUIZ.color.white,
            borderRadius: QUIZ.radius.card,
            px: "16px",
            py: "14px",
            mt: { xs: "16px", md: "23px" },
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            textAlign: "left",
        }}
    >
        <Typography component="span" aria-hidden sx={{ fontSize: 20, lineHeight: 1.2 }}>
            {icon}
        </Typography>
        <Typography
            sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: 14, md: 15 },
                color: QUIZ.color.ink,
                lineHeight: 1.45,
            }}
        >
            {children}
        </Typography>
    </Box>
);

/**
 * Issue #54 — the reassurance moment the health question exists for: telling
 * the owner their dog's condition was taken into account. Renders only when a
 * real issue was picked ("No health issues" and blank stay silent).
 *
 * STATUS: blocked. Per audit (Page A, Item #3), this card stays as-is pending
 * re-addition of the source quiz question it depends on — do not extend or
 * "fix" the merged copy here until that question exists again.
 */
const HealthReassuranceCard = ({ dogName, issues }: { dogName: string; issues: string[] }) => {
    if (!issues.length) return null;

    // List every selected issue by name — "skin issues (and 2 more)" read as
    // impersonal and undercut the personalisation the card exists for.
    // "Other" alone reads badly lowercased, so it maps to a generic phrase.
    const named = issues.map((i) =>
        i === HealthIssue.OTHER ? "the other issues you noted" : i.toLowerCase()
    );
    const issueList =
        named.length === 1
            ? named[0]
            : `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;

    return (
        <ReassuranceCard icon="🩺">
            {dogName} has <strong>{issueList}</strong>.
        </ReassuranceCard>
    );
};

/**
 * Standalone allergy card (Audit Page A, Item #3). Matches the pop-presell
 * pricing mockup: same anatomy as StatCard (icon / bold value / grey label /
 * divider / description), full-width below the stat grid, dashed outline to
 * flag it as a newly-added conditional card. Renders independently of health
 * issues — gated only on form.allergies.
 */
const AllergyCard = ({ dogName, allergies }: { dogName: string; allergies: string[] }) => {
    if (!allergies.length) return null;

    // "No allergies" is stored as the ALLERGY_NONE sentinel — never show it as an allergen.
    const named = allergies.filter((a) => a !== ALLERGY_NONE).map((a) => a.toLowerCase());
    const hasAllergies = named.length > 0;
    const allergyList =
        named.length === 1
            ? named[0]
            : `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
    const value = hasAllergies ? `No ${allergyList}` : "No allergies";

    return (
        <Box
            sx={{
                bgcolor: QUIZ.color.white,
                borderRadius: QUIZ.radius.card,
                border: `2px dashed ${QUIZ.color.blue}`,
                px: { xs: "16px", md: "18px" },
                py: { xs: "14px", md: "16px" },
                mt: { xs: "16px", md: "23px" },
                width: "100%",
                boxSizing: "border-box",
                textAlign: "left",
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "38px minmax(0, 1fr)", md: "42px minmax(0, 1fr)" },
                    alignItems: "center",
                    gap: { xs: "10px", md: "8px" },
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: { xs: 38, md: 42 },
                        height: { xs: 38, md: 42 },
                        borderRadius: "10px",
                        bgcolor: QUIZ.color.green,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        component="img"
                         src={quizAsset("icon-food")}
                        alt=""
                        sx={{ width: "70%", height: "70%", objectFit: "contain" }}
                    />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.display,
                            fontWeight: 900,
                            fontSize: { xs: 17, md: 18 },
                            color: QUIZ.color.ink,
                            lineHeight: 1.1,
                            overflowWrap: "break-word",
                        }}
                    >
                        {value}
                    </Typography>
                    <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 13, md: 14 }, color: QUIZ.color.statLabel }}>
                        Allergies
                    </Typography>
                </Box>
            </Box>

            {hasAllergies && (
                <>
                    <Box
                        sx={{
                            borderTop: `1px solid ${QUIZ.color.ink}`,
                            opacity: 0.2,
                            my: { xs: "10px", md: "12px" },
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontSize: { xs: 14, md: 15 },
                            color: QUIZ.color.ink,
                            lineHeight: 1.45,
                        }}
                    >
                        We've hidden every recipe containing {allergyList} from {dogName}'s menu.
                    </Typography>
                </>
            )}
        </Box>
    );
};

const ReproductionCard = ({
    dogName,
    sex,
    reproduction,
}: {
    dogName: string;
    sex?: string;
    reproduction?: string;
}) => {
    const normalizedReproduction =
        sex === Sex.FEMALE && reproduction === Reproduction.NEUTERED
            ? Reproduction.SPAYED
            : sex === Sex.FEMALE && reproduction === Reproduction.NOT_NEUTERED
                ? Reproduction.NOT_SPAYED
                : reproduction;
    const label = normalizedReproduction
        ? REPRO_LABEL[normalizedReproduction as keyof typeof REPRO_LABEL]
        : undefined;
    if (!label) return null;

    const isFixed = normalizedReproduction === Reproduction.SPAYED || normalizedReproduction === Reproduction.NEUTERED;
    const statusText = isFixed
        ? `${dogName}'s portions are adjusted for their ${label.toLowerCase()} status.`
        : `${dogName}'s portions are adjusted for their current hormone status.`;

    return (
        <ReassuranceCard icon="⚖️">
            <strong>{label}</strong>
            <br />
            {statusText}
        </ReassuranceCard>
    );
};



/**
 * Founder story module — "pop-loki" / "pop-story-rows" (Audit Page A, Item #5).
 * - Mobile: boxed card, contained width, rounded corners — photo stacked on
 *   top of a dark brown quote panel (same shape as the original mockup).
 *   Uses the parent's own horizontal padding rather than adding its own, so
 *   it doesn't shrink narrower than the rest of the page's content.
 * - Desktop: full-bleed edge-to-edge section — photo fills the left half all
 *   the way to the viewport edge, dark brown quote panel fills the right half
 *   all the way to the other edge. No rounded corners on desktop since
 *   nothing is boxed.
 * Global module (not per-dog), rendered once by ResultsStepSummary.
 */
const StoryRow = () => (
    <>
        {/* Heading — stays in the normal constrained container on both breakpoints */}
        <Box
            sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: 560, md: 960, lg: 1100, xl: 1200 },
                mx: "auto",
                px: { xs: "20px", md: "40px" },
                mt: { xs: "32px", md: "48px" },
                textAlign: "center",
            }}
        >
            <Typography
                sx={{
                    fontFamily: QUIZ.font.label,
                    fontSize: { xs: 16, md: 27 },
                    letterSpacing: "0.08em",
                    color: QUIZ.color.brown,
                }}
            >
                WHY WE STARTED
            </Typography>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: 26, md: 41 },
                    color: QUIZ.color.brown,
                    lineHeight: 1.15,
                    mt: "8px",
                }}
            >
                COOKED BY HAND,
                <br />
                IN WEST SUSSEX
            </Typography>
        </Box>

        {/* MOBILE — boxed card, contained + rounded, hidden on desktop.
            No extra px here — relies on the parent's own horizontal padding
            so the card matches the width of the rest of the page's content. */}
        <Box
            sx={{
                display: { xs: "block", md: "none" },
                mt: "20px",
                width: "100%",
                maxWidth: { xs: "100%", sm: 560 },
                mx: "auto",
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ borderRadius: QUIZ.radius.card, overflow: "hidden" }}>
                {/* Photo panel */}
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "4 / 5",
                        bgcolor: QUIZ.color.brown,
                        overflow: "hidden",
                    }}
                >
                    {quizAsset("story-sam-loki") && (
                        <Box
                            component="img"
                            src={quizAsset("story-sam-loki")}
                            alt="Sam with Loki in the West Sussex kitchen"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center 30%",
                                display: "block",
                                zIndex: 0,
                            }}
                        />
                    )}
                </Box>

                {/* Quote panel */}
                <Box
                    sx={{
                        bgcolor: QUIZ.color.dark,
                        px: "20px",
                        py: "24px",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontStyle: "italic",
                            fontSize: 16,
                            color: QUIZ.color.white,
                            lineHeight: 1.5,
                        }}
                    >
                        "I couldn't find a single food I was happy to serve to Loki, my
                        ridgeback, so we started cooking it by hand in our West Sussex
                        kitchen."
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.label,
                            fontSize: 16,
                            color: QUIZ.color.blue,
                            mt: "16px",
                        }}
                    >
                        SAM · FOUNDER
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontSize: 13,
                            color: QUIZ.color.cream3,
                            mt: "6px",
                        }}
                    >
                        Every meal is still cooked by hand, with 100% British meat.
                    </Typography>
                </Box>
            </Box>
        </Box>

       {/* DESKTOP — full-bleed edge-to-edge row, hidden on mobile */}
<Box
    sx={{
        display: { xs: "none", md: "flex" },
        width: "100vw",
        position: "relative",
        left: "50%",
        transform: "translateX(-50%)",
        mt: "28px",
        bgcolor: QUIZ.color.dark,   // dark now lives on the row itself, so it shows around the inset photo too
        alignItems: "center",
        py: { md: "40px", lg: "56px" },   // gives the inset photo room to breathe top/bottom
        px: { md: "140px", lg: "200px", xl: "260px" },   // gives the inset photo room to breathe on the left
        boxSizing: "border-box",
    }}
>
    {/* Photo — fixed, compact, roughly-square box with margin around it,
        sized/positioned to match the reference (not stretched, not edge-to-edge). */}
    <Box
        sx={{
            position: "relative",
            flexShrink: 0,
            width: { md: 380, lg: 440 },
            height: { md: 360, lg: 420 },
            bgcolor: QUIZ.color.brown,
            overflow: "hidden",
            borderRadius: "22px",
        }}
    >
        {quizAsset("story-sam-loki") && (
            <Box
                component="img"
                src={quizAsset("story-sam-loki")}

                alt="Sam with Loki in the West Sussex kitchen"
                sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center 30%",
                    display: "block",
                    zIndex: 0,
                }}
            />
        )}
    </Box>

    {/* Quote — fills remaining width, no longer needs its own bgcolor since
        the row itself is now dark. */}
    <Box
        sx={{
            flex: "1 1 auto",
            minWidth: 0,
            pl: { md: "48px", lg: "80px" },
        }}
    >
        <Typography
            sx={{
                fontFamily: QUIZ.font.body,
                fontStyle: "italic",
                fontSize: 18,
                color: QUIZ.color.white,
                lineHeight: 1.5,
                maxWidth: 480,
            }}
        >
            "I couldn't find a single food I was happy to serve to Loki, my
            ridgeback, so we started cooking it by hand in our West Sussex
            kitchen."
        </Typography>
        <Typography
            sx={{
                fontFamily: QUIZ.font.label,
                fontSize: 22,
                color: QUIZ.color.blue,
                mt: "16px",
            }}
        >
            SAM · FOUNDER
        </Typography>
        <Typography
            sx={{
                fontFamily: QUIZ.font.body,
                fontSize: 14,
                color: QUIZ.color.cream3,
                mt: "6px",
            }}
        >
            Every meal is still cooked by hand, with 100% British meat.
        </Typography>
    </Box>
</Box>
    </>
);
const TIMELINE_MILESTONES: TimelineMilestone[] = [
    {
        day: 30,
        badgeColor: QUIZ.color.purple,
        title: "First 30 days",
        description: "Smaller, firmer stools and a lot more enthusiasm at dinner time.",
        quote:
            "It smells and looks like real food. He's thriving, his poops are healthy, and he licks his plate clean.",
        reviewer: "Natalie",
    },
    {
        day: 60,
        badgeColor: QUIZ.color.green,
        title: "By 60 days",
        description: "A softer, glossier coat.",
        quote: "Better stools, her coat shines, and she has more energy.",
        reviewer: "Wendy",
    },
    {
        day: 90,
        badgeColor: QUIZ.color.blue,
        title: "By 90 days",
        description:
            "Steadier energy and a healthy weight, so vet check-ups become less stressful.",
        quote:
            "The food is good and my dog adores it! She has trimmed down to her ideal weight and her coat is lovely and shiny. It's worth every penny.",
        reviewer: "",
    },
];

/** Row of 5 filled green Trustpilot-style stars. */
const TrustpilotStars = () => (
    <Box sx={{ display: "flex", gap: "2px" }}>
        {Array.from({ length: 5 }).map((_, i) => (
            <Box
                key={i}
                sx={{
                    width: 20,
                    height: 20,
                    bgcolor: QUIZ.color.trustpilotGreen,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography sx={{ fontSize: 12, color: QUIZ.color.white, lineHeight: 1 }}>
                    ★
                </Typography>
            </Box>
        ))}
    </Box>
);

const TimelineReviewCard = ({
    quote,
    reviewer,
}: {
    quote: string;
    reviewer: string;
}) => (
    <Box
        sx={{
            bgcolor: QUIZ.color.white,
            borderRadius: QUIZ.radius.card,
            px: "16px",
            py: "14px",
            mt: "12px",
            textAlign: "left",
        }}
    >
        <TrustpilotStars />
        <Typography
            sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: 14, md: 15 },
                color: QUIZ.color.ink,
                lineHeight: 1.6,
                mt: "10px",
            }}
        >
            "{quote}"
        </Typography>
        <Typography
            sx={{
                fontFamily: QUIZ.font.body,
                fontSize: { xs: 13, md: 14 },
                color: QUIZ.color.statLabel,
                mt: "12px",
            }}
        >
            {reviewer ? `${reviewer} · ` : ""}Verified Trustpilot review
        </Typography>
    </Box>
);
const TimelineRung = ({ milestone }: { milestone: TimelineMilestone }) => (
    <Box sx={{ display: "flex", gap: "14px", textAlign: "left" }}>
        <Box
            sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: milestone.badgeColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: 15,
                    color: QUIZ.color.ink,
                }}
            >
                {milestone.day}
            </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pb: "8px" }}>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: 18, md: 22 },
                    color: QUIZ.color.brown,
                    textTransform: "uppercase",
                }}
            >
                {milestone.title}
            </Typography>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.body,
                    fontSize: { xs: 14, md: 18 },
                    color: QUIZ.color.ink,
                    mt: "4px",
                }}
            >
                {milestone.description}
            </Typography>
            <TimelineReviewCard quote={milestone.quote} reviewer={milestone.reviewer} />
        </Box>
    </Box>
);

/**
 * "pop-timeline" — 30/60/90-day health-improvement timeline (Audit Page A, Item #7).
 * Personalized eyebrow + heading use the dog's name, so this is rendered per-dog
 * from inside DogSummary rather than once globally like StoryRow.
 */
const Timeline = ({ dogName }: { dogName: string }) => (
    <Box
        sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 560, md: 680, lg: 860, xl: 960 },
            mx: "auto",
            mt: { xs: "24px", md: "40px" },
            mb: { xs: "32px", md: "56px" },
            textAlign: "center",
        }}
    >
        <Typography
            sx={{
                  fontFamily: QUIZ.font.label,
                fontSize: { xs: 16, md: 27 },
                letterSpacing: "0.51px", 
                lineHeight: 1.3,
                color: QUIZ.color.brown, 
            }}
        >
            REAL HEALTH IMPROVEMENTS
        </Typography>
        <Typography
            sx={{
                fontFamily: QUIZ.font.display,
                fontWeight: 900,
                fontSize: { xs: 26, md: 38 },
                color: QUIZ.color.brown,
                lineHeight: 1.15,
                mt: "8px",
                mb: { xs: "24px", md: "41px" },
            }}
        >
            WHAT CHANGES
            <br />
            FOR {dogName.toUpperCase()}
        </Typography>

        <Box sx={{ position: "relative" }}>
            {/* Connecting line running through the day badges */}
            <Box
                sx={{
                    position: "absolute",
                    left: "19px",
                    top: "20px",
                    bottom: "20px",
                    width: "2px",
                    bgcolor: QUIZ.color.brown,
                    opacity: 0.25,
                    zIndex: 0,
                }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "20px", md: "28px" }, position: "relative", zIndex: 1 }}>
                {TIMELINE_MILESTONES.map((m) => (
                    <TimelineRung key={m.day} milestone={m} />
                ))}
            </Box>
        </Box>
    </Box>
);
type ComparisonRow = {
    label: string;
    paws: boolean;
    col2: boolean;
    col3: boolean;
    col4: boolean;
    col5: boolean;
};

const COMPARISON_ROWS: ComparisonRow[] = [
    { label: "Hand-cooked at 90°", paws: true, col2: false, col3: false, col4: false, col5: false },
    { label: "100% British meat from trusted farmers", paws: true, col2: false, col3: false, col4: false, col5: false },
    { label: "Personalised menu for every dog", paws: true, col2: true, col3: false, col4: false, col5: true },
    { label: "0 plastic and mess", paws: true, col2: false, col3: false, col4: false, col5: false },
    { label: "Bespoke superfood supplements", paws: true, col2: false, col3: false, col4: false, col5: true },
];

const COMPARISON_LOGOS = [
    { key: "comparison-paws", alt: "Paws on Plates", isPaws: true },
    { key: "comparison-butternut", alt: "Butternut Box", isPaws: false },
    { key: "comparison-butchers", alt: "Butcher's", isPaws: false },
    { key: "comparison-tails", alt: "Tails.com", isPaws: false },
    { key: "comparison-years", alt: "Years", isPaws: false },
] as const;

const comparisonAssetUrls = {
    checkIcon: quizAsset("comparison-check"),
    crossIcon: quizAsset("comparison-cross"),
};

const CompareMark = ({ ok }: { ok: boolean }) => (
    <Box
        sx={{
           width: { xs: 28, md: 40 },
height: { xs: 28, md: 40 },
            borderRadius: "50%",
            bgcolor: ok ? QUIZ.color.ink : QUIZ.color.cream3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
        }}
    >
        <Box
            component="img"
            src={ok ? comparisonAssetUrls.checkIcon : comparisonAssetUrls.crossIcon}
            alt={ok ? "Yes" : "No"}
            sx={{
                width: { xs: 15, md: 17 },
                height: { xs: 15, md: 17 },
                objectFit: "contain",
                filter: ok
                    ? "brightness(0) saturate(100%) invert(88%) sepia(28%) saturate(768%) hue-rotate(31deg) brightness(104%) contrast(101%)"
                    : "none",
            }}
        />
    </Box>
);

const ComparisonSection = () => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const COL_WIDTH = 72;
    const MOBILE_HEADER_HEIGHT = 56;
    const MOBILE_ROW_HEIGHT = 78;

    const scrollBy = (direction: -1 | 1) => {
        scrollRef.current?.scrollBy({ left: direction * COL_WIDTH, behavior: "smooth" });
    };

    return (
        // Single brown parent wraps header + card. No absolute, no fixed height,
        // no negative margins — plain vertical flex column, width 100%.
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                bgcolor: QUIZ.color.brown, 
                px: { xs: "16px", md: "40px" },
                py: { xs: "32px", md: "40px" },
                boxSizing: "border-box",
                textAlign: "center",
            }}
        >
            {/* Header — sits directly in the same flow as the card below, no positioning tricks */}
            <Typography sx={{ fontFamily: QUIZ.font.label, fontSize: { xs: 22, md: 27 }, letterSpacing: "0.03em", lineHeight: 1.3, color: QUIZ.color.blue }}>
                THE FRESH FOOD AISLE
            </Typography>
            <Typography sx={{ fontFamily: QUIZ.font.display, fontWeight: 900, fontSize: { xs: 26, md: 41 }, color: QUIZ.color.cream, lineHeight: 1.15, mt: "8px" }}>
                HOW PAWS<br />COMPARES
            </Typography>
            <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 16, md: 17 }, color: QUIZ.color.cream, mt: "12px", maxWidth: 420 }}>
                Against the other fresh brands you're probably weighing up.
            </Typography>

            {/* Card: w-full max-w-xl, rounded-2xl, p-4, overflow-x-auto — sequential in the
                same flex column as the header, no absolute positioning or negative margin. */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "960px", // Tailwind max-w-xl
                    bgcolor: QUIZ.color.warmWhite, 
                    borderRadius: "16px", // rounded-2xl
                    p: "16px", // p-4
                    mt: { xs: "24px", md: "32px" },
                    boxSizing: "border-box",
                    
                    overflow: "hidden",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {/* ── DESKTOP ── */}
                <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "left", minWidth: "fit-content" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1.6fr repeat(5, 1fr)", columnGap: "8px", alignItems: "center" }}>
                        <Box />
                        {COMPARISON_LOGOS.map((logo) => (
                            <Box
                                key={logo.key}
                                sx={{
                                    bgcolor: logo.isPaws ? QUIZ.color.blue : "transparent",
                                    borderTopLeftRadius: logo.isPaws ? "10px" : 0,
                                    borderTopRightRadius: logo.isPaws ? "10px" : 0,
                                    minHeight: 56,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    py: "12px",
                                }}
                            >
                                <Box component="img" src={quizAsset(logo.key)} alt={logo.alt} sx={{ maxWidth: { xs: "88px", md: "110px" }, maxHeight: { xs: "46px", md: "56px" }, objectFit: "contain" }} />                            </Box>
                        ))}
                    </Box>
                    {COMPARISON_ROWS.map((row) => (
                        <Box key={row.label} sx={{ display: "grid", gridTemplateColumns: "1.6fr repeat(5, 1fr)", columnGap: "8px", alignItems: "center", borderTop: `1px solid ${QUIZ.color.cream3}` }}>
                            <Typography sx={{ fontFamily: QUIZ.font.body, fontWeight: 700, fontSize: { xs: 14, md: 17 }, color: QUIZ.color.grey, py: "12px" }}>                                {row.label}
                            </Typography>
                            {[row.paws, row.col2, row.col3, row.col4, row.col5].map((value, i) => (
                                <Box key={i} sx={{ bgcolor: i === 0 ? QUIZ.color.blue : "transparent", display: "flex", alignItems: "center", justifyContent: "center", py: { xs: "12px", md: "18px" }, minHeight: { xs: 51, md: 68 } }}>
                                    <CompareMark ok={value} />
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>            
                 {/* ── MOBILE: label + PAWS in a fixed, non-scrolling pane; competitor
                   logos in a separate, adjacent overflow-x:auto pane. This avoids
                    position:sticky entirely — sticky columns re-order/overlap once
                    multiple pinned columns interact with programmatic scroll (see
                    screenshot), because sticky offsets don't guarantee DOM order is
                    preserved once auto-scroll runs. A plain two-pane flex layout has
                    no such ambiguity: pinned content is a separate block, full stop. */}
                <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "flex-start", width: "100%" }}>
                    {/* Fixed pane — label + PAWS, never scrolls. PAWS column narrowed
                        from 96px to 72px, logo shrunk to fit, per feedback (was too wide). */}
                    <Box sx={{ flex: "0 0 auto", width: 112 + 8 + 72 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "112px 72px", columnGap: "8px", height: MOBILE_HEADER_HEIGHT }}>
                            <Box />
                            <Box sx={{ bgcolor: QUIZ.color.blue, borderTopLeftRadius: "10px", borderTopRightRadius: "10px", height: MOBILE_HEADER_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center", py: "12px", boxSizing: "border-box" }}>
                            <Box component="img" src={quizAsset("comparison-paws")} alt="Paws on Plates" sx={{ maxWidth: "56px", maxHeight: "30px", objectFit: "contain" }} />
                            </Box>
                        </Box>
                        {COMPARISON_ROWS.map((row) => (
                            <Box key={row.label} sx={{ display: "grid", gridTemplateColumns: "112px 72px", columnGap: "8px", alignItems: "center", height: MOBILE_ROW_HEIGHT, borderTop: `1px solid ${QUIZ.color.cream3}`, boxSizing: "border-box" }}>
                                <Typography sx={{ fontFamily: QUIZ.font.body, fontWeight: 700, fontSize: 12, lineHeight: 1.35, color: QUIZ.color.grey, py: "12px", pr: "6px", boxSizing: "border-box" }}>
                                    {row.label}
                                </Typography>
                                <Box sx={{ bgcolor: QUIZ.color.blue, height: MOBILE_ROW_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center", py: "12px", boxSizing: "border-box" }}>
                                    <CompareMark ok={row.paws} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    {/* Scrolling pane — competitors only. minWidth:0 lets this flex item
                        actually shrink to fit inside the card instead of blowing it out. */}
                    <Box
                        ref={scrollRef}
                        sx={{
                            flex: "1 1 auto",
                            minWidth: 0,
                            ml: "8px",
                            pr: "12px",
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                        }}
                    >
                        <Box sx={{ minWidth: 4 * COL_WIDTH + 3 * 8 }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: `repeat(4, ${COL_WIDTH}px)`, columnGap: "8px", height: MOBILE_HEADER_HEIGHT }}>
                                {COMPARISON_LOGOS.filter((l) => !l.isPaws).map((logo) => (
                        <Box key={logo.key} sx={{ height: MOBILE_HEADER_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center", py: "12px", boxSizing: "border-box" }}>
                            <Box component="img" src={quizAsset(logo.key)} alt={logo.alt} sx={{ maxWidth: "56px", maxHeight: "30px", objectFit: "contain" }} />
                            </Box>
                          ))}
                        </Box>
                            {COMPARISON_ROWS.map((row) => (
                                <Box key={row.label} sx={{ display: "grid", gridTemplateColumns: `repeat(4, ${COL_WIDTH}px)`, columnGap: "8px", height: MOBILE_ROW_HEIGHT, borderTop: `1px solid ${QUIZ.color.cream3}`, boxSizing: "border-box" }}>
                                    {[row.col2, row.col3, row.col4, row.col5].map((value, i) => (
                                        <Box key={i} sx={{ height: MOBILE_ROW_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center", py: "12px", boxSizing: "border-box" }}>
                                            <CompareMark ok={value} />
                                        </Box>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
 * ADD 1: new component — place near the other reassurance-style components,
 * e.g. right after ReassuranceCard / before ReproductionCard.
 * ────────────────────────────────────────────────────────────────────── */
 
type ProofStripItem = {
    icon: string;
    badgeColor: string;
    title: string;
    description: string;
};
 
const PROOF_STRIP_ITEMS: ProofStripItem[] = [
    {
        icon: "★",
        badgeColor: QUIZ.color.green,
        title: "Rated 94% by All About Dog Food",
        description: "Independently rated as the UK's best truly fresh food.",
    },
    {
        icon: "🐾",
        badgeColor: QUIZ.color.purple,
        title: "Over 1 million bowls licked clean",
        description: "By happier, healthier dogs across the UK.",
    },
    {
        icon: "🍲",
        badgeColor: QUIZ.color.blue,
        title: "Hand-cooked in West Sussex",
        description: "With 100% British meat for quality that can't be beaten.",
    },
];
 
/**
 * "Proof strip" — trust/rating badges block.
 * MOBILE: unchanged — icon-left, text-right rows stacked vertically.
 * DESKTOP (FIXED): was stacking the same vertical list just centered, which
 * left this looking identical to mobile with empty space either side. Now
 * lays the 3 items out as a row, each item icon-on-top/text-below like a
 * proper desktop trust strip.
 */
const ProofStrip = () => (
    <Box
        sx={{
            bgcolor: QUIZ.color.brown,
            borderRadius: 0,
            borderTop: `1px solid ${QUIZ.color.cream3}`,
            px: { xs: "28px", md: "40px" },
            py: { xs: "28px", md: "56px" },
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "20px", md: "40px" },
            textAlign: { xs: "left", md: "center" },
            maxWidth: { xs: "100%", md: 1100 },
            mx: "auto",
            alignItems: { xs: "stretch", md: "stretch" },
        }}
    >
        {PROOF_STRIP_ITEMS.map((item) => (
            <Box
                key={item.title}
                sx={{
                    display: "flex",
                    flexDirection: { xs: "row", md: "column" },
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: { xs: "12px", md: "10px" },
                    textAlign: { xs: "left", md: "center" },
                    flex: { xs: "initial", md: 1 },
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        flexShrink: 0,
                        width: { xs: 50, md: 56 },
                        height: { xs: 50, md: 56 },
                        borderRadius: "50%",
                        bgcolor: item.badgeColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography component="span" aria-hidden sx={{ fontSize: { xs: 22, md: 25 }, lineHeight: 1 }}>
                        {item.icon}
                    </Typography>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.display,
                            fontWeight: 900,
                            fontSize: { xs: 17, md: 21 },
                            color: QUIZ.color.white,
                            textTransform: "uppercase",
                            lineHeight: 1.25,
                        }}
                    >
                        {item.title}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontSize: { xs: 14, md: 15 },
                            color: QUIZ.color.cream3,
                            mt: "4px",
                            lineHeight: 1.4,
                        }}
                    >
                        {item.description}
                    </Typography>
                </Box>
            </Box>
        ))}
    </Box>
);
/**
 * "pop-story-video" — 4:5 muted, autoplay, looping video beneath the
 * founder story quote panel (Audit Page A, Item #6). muted + playsInline +
 * loop are required together for mobile browsers to allow autoplay.
 * Global module (not per-dog), same as StoryRow.
 */
const StoryVideo = () => (
    <Box
        sx={{
            width: "100%",
            // Keep the mobile story clip portrait, but use the wide desktop
            // frame from the supplied reference (864px by 532px).
            maxWidth: { xs: "100%", sm: 560, md: 864 },
            mx: "auto",
            mt: { xs: "16px", md: "24px" },
        }}
    >
        <Box
            sx={{
                position: "relative",
                width: "100%",
                aspectRatio: { xs: "4 / 5", md: "864 / 532" },
                borderRadius: QUIZ.radius.card,
                overflow: "hidden",
                bgcolor: QUIZ.color.cream3,
                boxShadow: { xs: "none", md: "0 12px 30px rgba(0,0,0,.12)" },
            }}
        >
            <Box
                component="video"
                autoPlay
                muted
                loop
                playsInline
                sx={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                }}
            >
                <source src={quizAsset("summary-story-video")} type="video/mp4" />
            </Box>
        </Box>
    </Box>
);
 
function DogSummary({ dogId }: { dogId: string }) {
    const { form = {}, update } = useDog(dogId);
    const sendQuizToKlaviyo = useSendQuizToKlaviyo();

    const normalizedForm: DogFormLike = useMemo(
        () => ({
            ...(form || {}),
            years:
                form?.years !== undefined && form.years !== "" ? Number(form.years) : undefined,
            months:
                form?.months !== undefined && form.months !== "" ? Number(form.months) : undefined,
            weightKg:
                form?.weightKg !== undefined && form.weightKg !== ""
                    ? Number(form.weightKg)
                    : undefined,
        }),
        [form]
    );

    const {
        name,
        weightKg,
        bodyCondition,
        treatFrequency,
        activityLevel,
    } = form;

    const calc = useMemo(() => calculateDailyIntakeFromForm(normalizedForm, { kcalPerGram: 1.65 }), [normalizedForm]);
    const { dailyCalories, gramsPerDay } = calc;
    const activityLevelMeta = ActivityLevelMeta[activityLevel as keyof typeof ActivityLevelMeta];
    useEffect(() => {
        if (form.dailyCalories !== dailyCalories || form.dailyGrams !== gramsPerDay) {
            update({
                dailyCalories,
                dailyGrams: gramsPerDay,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dailyCalories, gramsPerDay]);

    const yearsNum: number =
        normalizedForm.years !== undefined ? Number(normalizedForm.years) : 0;
    const monthsNum: number =
        normalizedForm.months !== undefined ? Number(normalizedForm.months) : 0;

    const ageLabel =
        yearsNum || monthsNum
            ? `${yearsNum ? `${yearsNum} year${yearsNum > 1 ? "s" : ""}` : ""} ${monthsNum ? `${monthsNum} month${monthsNum > 1 ? "s" : ""}` : ""
                }`.trim()
            : "N/A";
    const pronoun = getPronoun(form?.sex);
    const ageCategory = getAgeCategory(yearsNum, monthsNum);

    const descriptions = useMemo(
        () =>
            getStatDescriptions({
                name: name || "your dog",
                pronoun,
                bodyCondition,
                ageCategory,
                treatFrequency,
                activityLevel,
            }),
        [name, pronoun, bodyCondition, ageCategory, treatFrequency, activityLevel]
    );        

    function stableStringify(obj: any) {
        return JSON.stringify(obj, Object.keys(obj).sort());
    }

    async function sha1(input: string) {
        // Browser crypto
        const enc = new TextEncoder().encode(input);
        const buf = await crypto.subtle.digest("SHA-1", enc);
        return Array.from(new Uint8Array(buf))
            .map(b => b.toString(16).padStart(2, "0"))
            .join("");
    }

    function normList(x: unknown): string[] {
        if (!Array.isArray(x)) return [];
        return x.map(String).map(s => s.trim()).filter(Boolean).sort();
    }

    function stableStringifyDeep(obj: any) {
        const seen = new WeakSet();
        const sorter = (x: any): any => {
            if (x && typeof x === "object") {
                if (seen.has(x)) return null;
                seen.add(x);

                if (Array.isArray(x)) return x.map(sorter);
                return Object.keys(x).sort().reduce((acc: any, k) => {
                    acc[k] = sorter(x[k]);
                    return acc;
                }, {});
            }
            return x;
        };
        return JSON.stringify(sorter(obj));
    }

    const watchJson = useMemo(() => {
        const email = String(form?.ownerEmail || "").trim().toLowerCase();

        // IMPORTANT: include ALL fields you care about
        const dog = buildDogForStorage({
            ...form,
            id: dogId,
            name: safeStr(form?.name) || "Dog",
            breed: safeStr(form?.breed) || null,
            bodyCondition: bodyCondition || undefined,
            activityLevel: activityLevel || undefined,
            treatFrequency: treatFrequency || undefined,
            dailyGrams: gramsPerDay,
            dailyCalories,
            ageYears: yearsNum,
            ageMonths: monthsNum,
            allergies: normList(form?.allergies),
            healthIssues: normList(form?.healthIssues),
            weightKg:
                form?.weightKg !== undefined && form.weightKg !== ""
                    ? Number(form.weightKg)
                    : undefined,
        });

        return stableStringifyDeep({
            email,
            ownerName: safeStr(form?.ownerName) || null,
            quizId: safeStr(form?.quizId) || "paws-quiz",
            marketingOptIn: form?.marketingOptIn === true,
            dog,
        });
    }, [
        dogId,
        gramsPerDay,
        dailyCalories,
        yearsNum,
        monthsNum,

        form?.ownerEmail,
        form?.ownerName,
        form?.quizId,
        form?.marketingOptIn,

        form?.name,
        form?.breed,
        form?.sex,
        form?.reproduction,
        form?.bodyCondition,
        form?.activityLevel,
        form?.treatFrequency,
        form?.weightKg,
        form?.selectedPackSize,
        form?.selectedPlanDays,

        // arrays: use JSON string of normalized version as deps
        JSON.stringify(normList(form?.allergies)),
        JSON.stringify(normList(form?.healthIssues)),
    ]);
    function dogWatchKey(form: any, dogId: string, gramsPerDay: number, dailyCalories: number, yearsNum: number, monthsNum: number) {
        const dog = buildDogForStorage({
            ...form,
            id: dogId,
            dailyGrams: gramsPerDay,
            dailyCalories,
            ageYears: yearsNum,
            ageMonths: monthsNum,
            // harden arrays in case state mutates in-place
            allergies: normList(form?.allergies),
            healthIssues: normList(form?.healthIssues),
        });

        // only include fields you want to trigger updates
        return stableStringify({
            email: String(form?.ownerEmail || "").trim().toLowerCase(),
            ownerName: safeStr(form?.ownerName) || null,
            quizId: safeStr(form?.quizId) || "paws-quiz",
            marketingOptIn: form?.marketingOptIn === true,
            dog,
        });
    }

    useEffect(() => {
        if (!dailyCalories || !gramsPerDay) return;

        const email = String(form?.ownerEmail || "").trim().toLowerCase();
        if (!email) return;

        let cancelled = false;

        const t = window.setTimeout(() => {
            (async () => {
                const hash = await sha1(watchJson);

                const storageKey = `klaviyo_quiz_last_hash:${dogId}:${email}`;
                if (sessionStorage.getItem(storageKey) === hash) return;

                const payload = {
                    email,
                    ownerName: safeStr(form?.ownerName) || undefined,
                    quizId: safeStr(form?.quizId) || "paws-quiz",
                    source: "paws-quiz",
                    marketingOptIn: form?.marketingOptIn === true,
                    // Quiz tests (#51): arm on the Klaviyo profile so quiz
                    // takers who never buy can still be cut by arm.
                    quizTests: quizTestTags(window.__PAWS_EXPERIMENTS__),
                    dogs: [
                        buildDogForStorage({
                            ...form,
                            id: dogId,
                            dailyGrams: gramsPerDay,
                            dailyCalories,
                            ageYears: yearsNum,
                            ageMonths: monthsNum,
                            allergies: normList(form?.allergies),
                            healthIssues: normList(form?.healthIssues),
                        }),
                    ],
                };

                try {
                    await sendQuizToKlaviyo(payload);
                    if (cancelled) return;
                    sessionStorage.setItem(storageKey, hash);
                    // Klaviyo sync complete
                } catch (err: any) {
                    if (cancelled) return;
                    console.error("[Klaviyo] Failed to send quiz update", err?.message ?? err);
                }
            })();
        }, 600);

        return () => {
            cancelled = true;
            window.clearTimeout(t);
        };
    }, [watchJson, sendQuizToKlaviyo, dogId, gramsPerDay, dailyCalories]);

    const bodyLabel =
        bodyCondition && BODY_LABEL[bodyCondition as keyof typeof BODY_LABEL]
            ? BODY_LABEL[bodyCondition as keyof typeof BODY_LABEL]
            : bodyCondition || "N/A";
    const treatLabel =
        treatFrequency && TreatFrequencyMeta[treatFrequency as keyof typeof TreatFrequencyMeta]
            ? TreatFrequencyMeta[treatFrequency as keyof typeof TreatFrequencyMeta].label
            : treatFrequency || "N/A";
    const activityShort = activityLevelMeta
        ? activityLevelMeta.max === Infinity
            ? `${activityLevelMeta.min}+ mins`
            : `${activityLevelMeta.min}-${activityLevelMeta.max} mins`
        : "N/A";

    return (
        <Box sx={{ width: "100%", maxWidth: { xs: "100%", sm: 560, md: 680, lg: 860, xl: 960 }, mx: "auto", textAlign: "center" }}>
           {/* Heading */}
            <Typography sx={{ fontFamily: QUIZ.font.label, fontSize: { xs: 16, md: 19 }, color: QUIZ.color.ink }}>
                Your dog's profile
            </Typography>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: 30, md: 50 },
                    color: QUIZ.color.ink,
                    lineHeight: 1.1,
                    mt: "8px",
                }}
            >
                {name || "Your Dog"}
            </Typography>
            <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: { xs: 16, md: 18 }, color: QUIZ.color.ink, mt: { xs: "8px", md: "14px" } }}>
                Daily recommended calories
            </Typography>
            <Typography
                sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: 20, md: 22 },
                    color: QUIZ.color.ink,
                    mt: "4px",
                }}
            >
                {dailyCalories} kcal
            </Typography>

            {/* Stat grid — 2 × 3 on mobile (Figma 12), 3 × 2 on desktop (Figma 1586:10923) */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr" },
                    gridAutoRows: "1fr",
                    alignItems: "stretch",
                    gap: { xs: "16px", md: "23px" },
                    mt: { xs: "20px", md: "32px" },
                    width: "100%",
                }}
            >
                <StatCard
                    icon={quizAsset("icon-weight")}
                    label="Weight"
                    value={weightKg != null && weightKg !== "" ? `${weightKg} kg` : "N/A"}
                description={descriptions.weight}
                />
                <StatCard icon={quizAsset("icon-ideal")} label="Body type" value={bodyLabel} description={descriptions.bodyType} />
                <StatCard icon={quizAsset("icon-age")} label="Age" value={ageLabel} description={descriptions.age} />
                <StatCard icon={quizAsset("icon-treat")} label="Treats" value={treatLabel} description={descriptions.treats} />
                <StatCard
                    icon={quizAsset("icon-active")}
                    label="Activity per day"
                    value={activityShort}
                     description={descriptions.activity}
                />
                <StatCard icon={quizAsset("icon-food")} label="Daily grams" value={`${gramsPerDay} g`} description={descriptions.grams} />           

            </Box>
   

            {/* Reproduction status is collected in the basic quiz step. */}
            <ReproductionCard
                dogName={name || "your dog"}
                sex={form?.sex}
                reproduction={form?.reproduction}
            />

            {/* Allergy card — split out on its own condition (Audit Page A, Item #3) */}
            <AllergyCard
                dogName={name || "your dog"}
                allergies={normList(form?.allergies)}
            />

            {/* Issue #54: health-issues reassurance — BLOCKED, see comment above component */}
            <HealthReassuranceCard
                dogName={name || "your dog"}
                issues={(form?.healthIssues ?? []).filter((h: string) => h !== HEALTH_ISSUE_NONE)}
            />
        </Box>
        
    );
}

export default function ResultsStepSummary({ dogs, onNext }: Props) {
    if (!dogs || dogs.length === 0) return null;

    const dogNames = dogs.map((dog, index) => {
        const name = String(dog.form?.name || "").trim();
        if (name) return name;
        return index === 0 ? "Dog" : `Dog ${String.fromCharCode(65 + index)}`;
    });
    const planLabel = dogNames.length === 1
        ? `See ${dogNames[0]}'s plan`
        : `See ${dogNames.join(" & ")}'s plan`;

    return (
    <>
        {/* Part 1 — dog summaries + CTA, contained/clipped as before */}
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 3, md: 6 },
                px: { xs: 1.5, sm: 2, md: 0 },
                overflowX: "hidden",
                width: "100%",
                boxSizing: "border-box",
            }}
        >
            {dogs.map((dog) => (
                <DogSummary key={dog.id} dogId={dog.id} />
            ))}
            <Box
                sx={{
                    mt: { xs: "16px", md: "23px" },
                    width: "100%",
                    maxWidth: { xs: "100%", md: 550 },
                     mb: { xs: "32px", md: "48px" },
                    mx: "auto",
                }}
            >
                <QuizInlineCTA onClick={onNext} label={planLabel} />
            </Box>
        </Box>

        {/* StoryRow — full-bleed on desktop, so it must sit outside any
            overflowX:hidden wrapper. Placed here so it renders above the
            video and timeline, per the requested order. */}
        <StoryRow />

        {/* Part 2 — video + timeline, contained/clipped as before, resumes
            right after StoryRow */}
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 3, md: 6 },
                px: { xs: 1.5, sm: 2, md: 0 },
                overflowX: "hidden",
                width: "100%",
                boxSizing: "border-box",
                mt: { xs: 3, md: 6 },
            }}
        >
            <StoryVideo />
            <Timeline
                dogName={dogs.length > 1 ? "your dogs" : dogs[0].form?.name || "your dog"}
            />
        </Box>

        {/* Full-bleed section — kept OUTSIDE the overflowX:hidden wrappers above, otherwise the 100vw break-out gets clipped back to the content width. */}
        <Box
            sx={{
                bgcolor: QUIZ.color.brown,
                width: "100vw",
                position: "relative",
                left: "50%",
                transform: "translateX(-50%)",
           mb: {
            xs: "calc(-24px - var(--quizFooterH) - env(safe-area-inset-bottom))",
            md: "calc(-48px - var(--quizFooterH) - env(safe-area-inset-bottom))",
        },
        pb: {
            xs: "calc(24px + var(--quizFooterH) + env(safe-area-inset-bottom))",
            md: "calc(48px + var(--quizFooterH) + env(safe-area-inset-bottom))",
        },
    }}
>
            <ComparisonSection />
            <ProofStrip />
        </Box>
    </>
);
}
